import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AirplaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTrips } from "../context/TripsContext";
import useFlightBooking from "../hooks/useFlightBooking";
import type { FlightData } from "../@types";
import {
  CheckoutResume,
  clearCheckoutResume,
  readCheckoutResume,
} from "../utils/checkoutResume";

/** A domestic booking nobody has paid for yet. */
const UNPAID_STATUS = "initiated";

/**
 * Offers to continue a flight checkout after a setup task that left the site
 * (Mono bank linking, Paystack card linking) and reloaded the app.
 *
 * The saved id is only a pointer: the booking is re-fetched from the server
 * (which only returns the signed-in customer's own bookings) and restored only
 * while it is still unpaid. Payment endpoints re-check ownership and
 * already-paid state on their own.
 */
const CheckoutResumeBanner = () => {
  const { user } = useAuth();
  const { flightData, restoreBooking } = useTrips();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [entry, setEntry] = useState<CheckoutResume | null>(null);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    setEntry(readCheckoutResume(user?.id));
  }, [user?.id, pathname]);

  const { refreshFlightBooking } = useFlightBooking(entry?.bookingId, {
    enabled: false,
    refetchOnWindowFocus: false,
  });

  // Nothing to offer, or the booking is still in memory (no reload happened).
  if (!entry || flightData) return null;

  const dismiss = () => {
    clearCheckoutResume();
    setEntry(null);
  };

  const resume = async () => {
    setResuming(true);
    const result = await refreshFlightBooking();
    const booking = result.data?.data as unknown as FlightData | undefined;
    const usable =
      !result.isError &&
      !!booking &&
      String(booking.user_id) === String(user?.id) &&
      String(booking.status).toLowerCase() === UNPAID_STATUS;

    clearCheckoutResume();
    setEntry(null);
    setResuming(false);

    if (!usable) {
      toast.error(
        "We couldn't continue that booking. It may have expired or already been paid. Please search for your flight again."
      );
      return;
    }
    restoreBooking(booking);
    navigate(entry.returnTo, { state: entry.returnState ?? null });
  };

  return (
    <div className="px-5 md:px-10 pt-4">
      <div
        role="status"
        className="max-w-6xl mx-auto rounded-xl border border-visaro-orange/40 bg-white shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <span className="flex items-center justify-center size-10 rounded-full bg-visaro-orange/10 text-visaro-orange shrink-0">
          <AirplaneTilt size={20} weight="fill" />
        </span>
        <div className="flex-1 text-sm text-deepBlue">
          <div className="font-semibold">You have a flight booking in progress</div>
          <div className="text-gray-600">
            Pick up where you left off and choose how to pay.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resume}
            disabled={resuming}
            className="p-2 px-5 rounded-lg font-medium bg-deepBlue text-white text-sm disabled:opacity-50"
          >
            {resuming ? "Checking…" : "Continue booking"}
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="p-2 rounded-full hover:bg-gray-100 transition"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutResumeBanner;
